/**
 * First-sign-in consent (MM-D-138, TMMAPP-0119 §A4; TMMAPI-0156 U1). A SUBSCRIBER (not locked)
 * whose `GET /me/consents` lacks a `TERMS` or `AUTO_RENEWAL` row for the served
 * `consumerTermsVersion` must accept before using the app; an admin-created subscription records
 * none. The version is the bootstrap's; with none served there is nothing to compare, and nothing
 * is asked.
 *
 * TMMAPP-0123 §C:
 * - A `COMP` subscription (`/me/billing.subscription.origin`) is never asked: there is no renewal
 *   charge to consent to (item 10). With no served `subscription.renewal`, only the terms are asked
 *   (TMMAPP-0124 item 6). Subscribers from before the consent unit are exempted by
 *   tmm-api's backfill of their records; the SPA adds no exemption of its own.
 * - Until the first read settles the gate is `checking`, and the page under it is held (item 13).
 * - A failed read asks nothing for now, never blocking a loaded session, but it is not the answer
 *   for the session: it is retried once after a short delay, and again on each route change, until
 *   one read succeeds (item 11).
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { billingApi } from '@/api/billing';
import { consentsApi, missingConsentKinds, type ConsentKind } from '@/api/consents';
import { bootstrapConsumerTerms } from '@/features/billing/useConsumerTerms';
import { useMe } from './meHooks';

/** The one timed retry after a failed read. */
export const CONSENT_RETRY_MS = 3000;

export type ConsentGate =
  | { kind: 'none' }
  | { kind: 'checking' }
  | { kind: 'required'; version: string; missing: ConsentKind[]; accepted: () => void };

type Read =
  | { key: string; status: 'ok'; missing: ConsentKind[] }
  | { key: string; status: 'failed'; timedRetryDone: boolean };

/**
 * What is missing for `version`; nothing for a `COMP` subscription. `/me/billing` is read only
 * when something is missing (its origin decides whether to ask at all).
 */
async function readMissing(version: string): Promise<ConsentKind[]> {
  const missing = missingConsentKinds(await consentsApi.list(), version);
  if (missing.length === 0) return missing;
  const subscription = (await billingApi.profile()).subscription;
  if (subscription?.origin === 'COMP') return [];
  // No served renewal (cancelled, ending): nothing renews, so auto-renewal is not asked (TMMAPP-0124
  // item 6); the terms still are.
  return subscription?.renewal == null
    ? missing.filter((kind) => kind !== 'AUTO_RENEWAL')
    : missing;
}

export function useConsentGate(eligible: boolean): ConsentGate {
  const me = useMe();
  const { pathname } = useLocation();
  const version = bootstrapConsumerTerms().version;
  const active = eligible && me.tier === 'SUBSCRIBER' && !me.locked && version !== null;
  // Read per person and version until one read succeeds; `[]` once nothing is missing.
  const readKey = active ? `${me.user.id ?? ''}|${version}` : null;
  const [read, setRead] = useState<Read | null>(null);
  // Bumped to ask again: the timed retry, or a route change after a failure.
  const [attempt, setAttempt] = useState(0);

  const current = read !== null && read.key === readKey ? read : null;
  const settledOk = current?.status === 'ok';

  useEffect(() => {
    if (readKey === null || version === null || settledOk) return;
    let cancelled = false;
    void readMissing(version)
      .then((missing) => {
        if (!cancelled) setRead({ key: readKey, status: 'ok', missing });
      })
      .catch(() => {
        if (cancelled) return;
        setRead((prev) => ({
          key: readKey,
          status: 'failed',
          timedRetryDone:
            prev !== null && prev.key === readKey && prev.status === 'failed'
              ? prev.timedRetryDone
              : false,
        }));
      });
    return () => {
      cancelled = true;
    };
    // `attempt` re-runs the read after a failure.
  }, [readKey, version, settledOk, attempt]);

  // The one timed retry after the first failure.
  const failedOnce = current?.status === 'failed' && !current.timedRetryDone;
  useEffect(() => {
    if (!failedOnce) return;
    const timer = window.setTimeout(() => {
      setRead((prev) =>
        prev !== null && prev.status === 'failed' ? { ...prev, timedRetryDone: true } : prev,
      );
      setAttempt((n) => n + 1);
    }, CONSENT_RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [failedOnce]);

  // Each route change after a failure asks again.
  const [seenPath, setSeenPath] = useState(pathname);
  if (pathname !== seenPath) {
    setSeenPath(pathname);
    if (current?.status === 'failed') setAttempt((n) => n + 1);
  }

  const accepted = useCallback(() => {
    if (readKey !== null) setRead({ key: readKey, status: 'ok', missing: [] });
  }, [readKey]);

  if (readKey === null || version === null) return { kind: 'none' };
  if (current === null) return { kind: 'checking' };
  if (current.status === 'failed' || current.missing.length === 0) return { kind: 'none' };
  return { kind: 'required', version, missing: current.missing, accepted };
}
